<template>
  <div class="task-list">
    <header>
      <div class="font-topic">{{ taskGroup ? taskGroup.name : "任务" }}</div>
      <div class="tool-box">
        <search-box from="todoList" :project-id="projectId" @search="setSearchInfo"></search-box>
        <base-button v-if="from === 'project' && hasCreateTask" type="primary" @click="createTask">
          新建
        </base-button>
      </div>

      <span class="shortcut-wrap">
        <span class="pointer" style="margin-right: 5px;" @click="toggleTaskExpansion">
          {{ expand ? "折叠" : "展开" }}所有任务
        </span>
        <dropdown-menu
          ref="sortKeysMenu"
          :menu-list="sortKeys"
          :margin-top="7"
          width="140"
          popover-class="task-list-custom-menu"
          @click="selectSort('key', $event)"
        >
          <template v-slot:default>
            <span>{{ currentSortText }}</span>
          </template>
          <template v-slot:menuItem="{ menu }">
            <div class="menu-item" :class="{ selected: menu.command === currentSort.key }">
              {{ menu.title }}
            </div>
          </template>
        </dropdown-menu>
        <span
          v-if="currentSort.type === 'ASC'"
          class="sort-icon"
          @click="selectSort('type', 'DESC')"
        >
          <svg-icon icon-class="svgo_sort-asc"></svg-icon>
        </span>
        <span v-else class="sort-icon" @click="selectSort('type', 'ASC')">
          <svg-icon icon-class="svgo_sort-desc"></svg-icon>
        </span>
      </span>
    </header>
    <tab
      v-if="selectedTasks.length == 0"
      :tab-list="tabs"
      :default-active="currentTab"
      @click="changeTab"
      class="tabs"
    ></tab>
    <batch-operation
      v-else
      :project-id="projectId"
      :task-group="taskGroup"
      :selected-tasks="selectedTasks"
      @data-changed="init"
    ></batch-operation>
    <task-table
      ref="taskTable"
      show-add-btn
      :show-selection="taskGroup != null"
      :from="from"
      :list-type="currentTab"
      :task-group="taskGroup"
      :project-id="projectId"
      :search-info="searchInfo"
      @selection-change="selectionChange"
    ></task-table>
    <edit-dialog
      v-if="from === 'project'"
      from="create"
      :project-id="projectId"
      :task-group="taskGroup"
      :visible.sync="editDialogVisible"
      @after-submit="handleCreateOrEdit"
    ></edit-dialog>
  </div>
</template>

<script>
import BaseButton from "@/components/UIComponents/Button"
import Tab from "@/components/UIComponents/Tab"
import EditDialog from "@/components/Task/EditDialog"
import DropdownMenu from "@/components/UIComponents/DropdownMenu"
import SearchBox from "./SearchBox"
import TaskTable from "./TaskTable"
import BatchOperation from "./BatchOperation"
import { tabs, todoListTabs, sortKeys } from "./static"
import { getTaskSortType, setTaskSortType } from "@/utils/project"
import { getProject, getProjectMembers } from "@/api/project/project"
import { Message } from "element-ui"

export default {
  name: "task-list",
  components: {
    BaseButton,
    Tab,
    TaskTable,
    SearchBox,
    DropdownMenu,
    EditDialog,
    BatchOperation
  },
  props: {
    taskGroup: Object,
    from: {
      type: String,
      default: "project" // project/todoList
    }
  },
  data() {
    return {
      sortKeys,
      tabs: this.from === "project" ? tabs : todoListTabs,
      currentTab: this.from === "project" ? "ALL" : "MY_RUNNING",
      searchInfo: {
        searchKey: null,
        searchValue: null
      },
      hasCreateTask: false,
      editDialogVisible: false,
      projectId: this.$route.params.projectId,
      selectedTasks: [],
      currentSort: { key: null, type: null },
      expand: true
    }
  },
  computed: {
    currentSortText() {
      if (!this.currentSort.key) return ""
      return this.sortKeys.find(type => type.command === this.currentSort.key).title
    },
    curUser() {
      return this.$store.getters.userInfo
    }
  },
  watch: {
    "$route.params.projectId": function(newVal, oldVal) {
      if (newVal != oldVal) this.projectId = this.$route.params.projectId
    }
  },
  methods: {
    init() {
      this.currentSort = getTaskSortType(this.projectId)
      if (!this.currentSort?.key) {
        this.currentSort = { key: sortKeys[0].command, type: "DESC" }
        setTaskSortType(this.projectId, this.currentSort)
      }
      if (this.from === "project") this.isHasCreateTask()
      this.$refs.taskTable?.init()
    },
    async isHasCreateTask() {
      // 获取新建权限
      let createRoleList = await getProject(this.projectId).then(res => res?.privilege?.roleList)
      if (createRoleList === null || createRoleList === undefined || createRoleList.length === 0) {
        this.hasCreateTask = true
        return
      }
      // 获取用户权限
      let userRoleList = await getProjectMembers(this.projectId).then(members => {
        return members.find(member => member.userId === this.curUser.userId).roles
      })
      if (this._.intersection(createRoleList, userRoleList).length > 0) this.hasCreateTask = true
      else this.hasCreateTask = false
    },
    changeTab(tab) {
      if (this.currentTab === tab) {
        this.init()
      } else {
        this.currentTab = tab
      }
    },
    setSearchInfo(searchKey, searchValue) {
      this.searchInfo = {
        searchKey,
        searchValue
      }
    },
    async createTask() {
      this.editDialogVisible = true
    },
    handleCreateOrEdit() {
      this.$refs.taskTable.refreshTable()
    },
    selectionChange(selectedTasks) {
      this.selectedTasks = selectedTasks || []
    },
    selectSort(command, value) {
      if (command === "key" && this.currentSort.key === value) return
      this.currentSort[command] = value
      setTaskSortType(this.projectId, this.currentSort)
      this.$refs.taskTable.refreshTable()
    },
    toggleTaskExpansion() {
      this.expand = !this.expand
      this.$refs.taskTable.toggleExpansion(this.expand)
    }
  }
}
</script>

<style lang="scss" scoped>
.task-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  header {
    padding: 10px 18px;
    display: flex;
    align-items: center;
    position: relative;
    .tool-box {
      margin-left: auto;
      display: flex;
      .search-box {
        margin-right: 6px;
      }
      .base-button {
        text-align: center;
        width: 64px;
        padding: 0;
      }
    }
  }
  .shortcut-wrap {
    z-index: 10;
    position: absolute;
    right: 23px;
    bottom: -30px;
    font-size: 14px;
    .dropdown-menu {
      cursor: pointer;
    }
    .sort-icon {
      margin-left: 3px;
      cursor: pointer;
      &:hover {
        color: $color-main;
      }
    }
  }
  .tabs {
    margin-left: 16px;
    height: 40px;
    box-sizing: border-box;
    padding-top: 10px;
  }
  .task-table {
    flex: 1 1;
  }
}
.task-list-custom-menu {
  .menu-item {
    height: 40px;
    padding: 12px 25px 12px;
    box-sizing: border-box;
    cursor: pointer;
    &:hover {
      background-color: #f5f7fa;
    }
    &.selected {
      color: $color-main;
      background-color: #f5f7fa;
    }
  }
}
</style>
